import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { AtsEditCandidateModalFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpdateCandidate } from 'modules/ats/hooks/candidates/useUpdateCandidate'

interface EditCandidateFormValues {
  firstName: string
  lastName: string
  email?: string
  phoneNumber?: string
  personalUrl?: string
}

interface Props {
  candidate: AtsEditCandidateModalFragment
  onClose: () => void
}

export const EditCandidateModal = ({ candidate, onClose }: Props) => {
  const { updateCandidate } = useUpdateCandidate(candidate)

  const form = useForm<EditCandidateFormValues>({
    enableReinitialize: true,
    defaultValues: {
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      email: candidate.email || '',
      phoneNumber: candidate.phoneNumber || '',
      personalUrl: candidate.personalUrl || '',
    },
    validate(data) {
      const errors: FormErrors<EditCandidateFormValues> = {}

      if (!data.firstName) {
        errors.firstName = i18n.t('common.empty')
      }

      if (!data.lastName) {
        errors.lastName = i18n.t('common.empty')
      }

      return errors
    },
    async onSubmit(data, { setErrors }) {
      const result = await updateCandidate(data)
      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      illustration="user-edit"
      title={i18n.t('ats.candidates.candidate_form.title')}
      description={i18n.t('ats.candidates.candidate_form.description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={form.isSubmitting || !form.isValid} submit>
            {i18n.t('ats.candidates.candidate_form.save')}
          </Button>
        </>
      }
    >
      <Box gap="s16" width="full">
        <Box flexDirection="row" gap="s24">
          <TextField
            mandatory
            label={i18n.t('ats.candidate_details.personal.name')}
            value={form.values.firstName}
            onChange={(value) => form.setFieldValue('firstName', value)}
            error={form.errors.firstName}
          />
          <TextField
            mandatory
            label={i18n.t('ats.candidate_details.personal.last_name')}
            value={form.values.lastName}
            onChange={(value) => form.setFieldValue('lastName', value)}
            error={form.errors.lastName}
          />
        </Box>
        <TextField
          label={i18n.t('ats.candidate_details.personal.email')}
          value={form.values.email}
          onChange={(value) => form.setFieldValue('email', value)}
          error={form.errors.email}
        />
        <TextField
          label={i18n.t('ats.candidate_details.personal.personal_url')}
          value={form.values.personalUrl}
          onChange={(value) => form.setFieldValue('personalUrl', value)}
          error={form.errors.personalUrl}
        />
        <TextField
          label={i18n.t('ats.candidate_details.personal.phone')}
          value={form.values.phoneNumber}
          onChange={(value) => form.setFieldValue('phoneNumber', value)}
          error={form.errors.phoneNumber}
        />
      </Box>
    </Modal>
  )
}
